import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Grid,
    Card,
    CardContent,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    Switch,
    Snackbar,
    IconButton,
    Tooltip
} from '@mui/material';
import { 
    Sync as SyncIcon, 
    Refresh as RefreshIcon, 
    Add as AddIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface OperationUnit {
    id: string;
    code: string;
    name: string;
    description: string;
    authority_id: string;
    hospital_id: string;
    site_id: string;
    type: number;
    enabled: boolean;
    active: boolean;
    disconnected: boolean;
    address: string;
    city: string;
    province: string;
    cap: string;
    phone_number: string;
    email: string;
    created_at: string;
    updated_at: string;
}

interface Site {
    id: string;
    name: string;
}

interface Hospital {
    id: string;
    name: string;
}

interface CreateOperationUnitForm {
    ministerialCode: string;
    name: string;
    regionalCode: string;
    sedeContextIdentifierType: 'CODICE_MINISTERIALE' | 'CODICE_REGIONALE' | 'ID_INTERNO';
    sedeIdentifier: string;
    startDate: string;
    endDate?: string;
    disconnected: boolean;
    printHeader?: string;
    printFooter?: string;
}

const OperationUnitsPage: React.FC = () => {
    const [operationUnits, setOperationUnits] = useState<OperationUnit[]>([]);
    const [sites, setSites] = useState<Site[]>([]);
    const [hospitals, setHospitals] = useState<Hospital[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [selectedSite, setSelectedSite] = useState('');
    const [selectedHospital, setSelectedHospital] = useState('');
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    
    // Create/Edit Dialog
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editingUnit, setEditingUnit] = useState<OperationUnit | null>(null);
    const [createForm, setCreateForm] = useState<CreateOperationUnitForm>({
        ministerialCode: '',
        name: '',
        regionalCode: '',
        sedeContextIdentifierType: 'CODICE_REGIONALE',
        sedeIdentifier: '',
        startDate: new Date().toISOString().split('T')[0],
        disconnected: false
    });
    const [editForm, setEditForm] = useState<Partial<CreateOperationUnitForm>>({});
    const [formLoading, setFormLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    const fetchOperationUnits = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                limit: rowsPerPage.toString(),
                offset: (page * rowsPerPage).toString()
            });
            
            if (selectedSite) {
                params.append('siteId', selectedSite);
            }
            
            if (selectedHospital) {
                params.append('hospitalId', selectedHospital);
            }

            const response = await apiClient.get(`/recup-sync/operation-units?${params}`);
            if (response.data.success) {
                setOperationUnits(response.data.data);
                setTotalCount(response.data.pagination.total);
            } else {
                setError(response.data.error || 'Errore nel recupero delle operation units');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nel recupero delle operation units');
        } finally {
            setLoading(false);
        }
    };

    const fetchSites = async () => {
        try {
            const response = await apiClient.get('/recup-sync/sites?limit=1000');
            if (response.data.success) {
                setSites(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching sites:', err);
        }
    };

    const fetchHospitals = async () => {
        try {
            const response = await apiClient.get('/recup-sync/hospitals?limit=1000');
            if (response.data.success) {
                setHospitals(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching hospitals:', err);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        try {
            const response = await apiClient.post('/recup-sync/sync/operation-units');
            if (response.data.success) {
                await fetchOperationUnits();
                setSnackbar({ 
                    open: true, 
                    message: `Sincronizzazione completata! Creati: ${response.data.created}, Aggiornati: ${response.data.updated}`, 
                    severity: 'success' 
                });
            } else {
                setError(response.data.error || 'Errore nella sincronizzazione');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione');
        } finally {
            setSyncing(false);
        }
    };

    const handleCreateUnit = async () => {
        setFormLoading(true);
        try {
            const response = await apiClient.post('/operation-units/create-recup', createForm);
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Unità operativa creata con successo su RECUP!', 
                    severity: 'success' 
                });
                setShowCreateDialog(false);
                setCreateForm({
                    ministerialCode: '',
                    name: '',
                    regionalCode: '',
                    sedeContextIdentifierType: 'CODICE_REGIONALE',
                    sedeIdentifier: '',
                    startDate: new Date().toISOString().split('T')[0],
                    disconnected: false
                });
                await fetchOperationUnits();
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nella creazione', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nella creazione', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleEditUnit = (unit: OperationUnit) => {
        setEditingUnit(unit);
        setEditForm({
            name: unit.name,
            ministerialCode: '',  // Will need to be filled by user
            regionalCode: unit.code,
            startDate: new Date().toISOString().split('T')[0],
            disconnected: unit.disconnected
        });
        setShowEditDialog(true);
    };

    const handleUpdateUnit = async () => {
        if (!editingUnit) return;
        
        setFormLoading(true);
        try {
            const response = await apiClient.put(`/operation-units/${editingUnit.id}/update-recup`, editForm);
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Unità operativa aggiornata con successo su RECUP!', 
                    severity: 'success' 
                });
                setShowEditDialog(false);
                setEditingUnit(null);
                setEditForm({});
                await fetchOperationUnits();
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nell\'aggiornamento', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nell\'aggiornamento', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusChip = (unit: OperationUnit) => {
        if (unit.disconnected) {
            return <Chip label="Disconnesso" color="error" size="small" />;
        }
        if (!unit.active) {
            return <Chip label="Inattivo" color="warning" size="small" />;
        }
        if (!unit.enabled) {
            return <Chip label="Disabilitato" color="default" size="small" />;
        }
        return <Chip label="Attivo" color="success" size="small" />;
    };

    const getTypeLabel = (type: number) => {
        switch (type) {
            case 1:
                return 'Ambulatorio';
            case 2:
                return 'Laboratorio';
            case 3:
                return 'Radiologia';
            default:
                return `Tipo ${type}`;
        }
    };

    const getSiteName = (siteId: string) => {
        const site = sites.find(s => s.id === siteId);
        return site?.name || siteId;
    };

    const getHospitalName = (hospitalId: string) => {
        const hospital = hospitals.find(h => h.id === hospitalId);
        return hospital?.name || hospitalId;
    };

    useEffect(() => {
        fetchSites();
        fetchHospitals();
    }, []);

    useEffect(() => {
        fetchOperationUnits();
    }, [page, rowsPerPage, selectedSite, selectedHospital]);

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Gestione Operation Units
            </Typography>

            {/* Statistiche */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Totale Operation Units
                            </Typography>
                            <Typography variant="h5">
                                {totalCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Units Attive
                            </Typography>
                            <Typography variant="h5">
                                {operationUnits.filter(u => u.active && u.enabled && !u.disconnected).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Ambulatori
                            </Typography>
                            <Typography variant="h5">
                                {operationUnits.filter(u => u.type === 1).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Radiologia
                            </Typography>
                            <Typography variant="h5">
                                {operationUnits.filter(u => u.type === 3).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Controlli */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        select
                        label="Filtra per Hospital"
                        value={selectedHospital}
                        onChange={(e) => {
                            setSelectedHospital(e.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 200 }}
                        size="small"
                    >
                        <MenuItem value="">Tutti gli hospitals</MenuItem>
                        {hospitals.map((hospital) => (
                            <MenuItem key={hospital.id} value={hospital.id}>
                                {hospital.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    
                    <TextField
                        select
                        label="Filtra per Site"
                        value={selectedSite}
                        onChange={(e) => {
                            setSelectedSite(e.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 200 }}
                        size="small"
                    >
                        <MenuItem value="">Tutti i sites</MenuItem>
                        {sites.map((site) => (
                            <MenuItem key={site.id} value={site.id}>
                                {site.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    
                    <Button
                        variant="outlined"
                        onClick={fetchOperationUnits}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                    >
                        Aggiorna
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={() => setShowCreateDialog(true)}
                        startIcon={<AddIcon />}
                        color="secondary"
                    >
                        Crea Operation Unit
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={handleSync}
                        disabled={syncing}
                        startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        color="primary"
                    >
                        {syncing ? 'Sincronizzazione...' : 'Sincronizza da RECUP'}
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Tabella Operation Units */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Codice</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrizione</TableCell>
                                <TableCell>Site</TableCell>
                                <TableCell>Hospital</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Stato</TableCell>
                                <TableCell>Ubicazione</TableCell>
                                <TableCell>Contatti</TableCell>
                                <TableCell>Creato</TableCell>
                                <TableCell>Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={12}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : operationUnits.length === 0 ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={12}>
                                        Nessuna operation unit trovata
                                    </TableCell>
                                </TableRow>
                            ) : (
                                operationUnits.map((unit) => (
                                    <TableRow key={unit.id}>
                                        <TableCell>{unit.id}</TableCell>
                                        <TableCell>{unit.code}</TableCell>
                                        <TableCell>{unit.name}</TableCell>
                                        <TableCell>{unit.description}</TableCell>
                                        <TableCell>{getSiteName(unit.site_id)}</TableCell>
                                        <TableCell>{getHospitalName(unit.hospital_id)}</TableCell>
                                        <TableCell>{getTypeLabel(unit.type)}</TableCell>
                                        <TableCell>{getStatusChip(unit)}</TableCell>
                                        <TableCell>
                                            {unit.address && (
                                                <div>
                                                    <div>{unit.address}</div>
                                                    <div>{unit.city} {unit.cap} ({unit.province})</div>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {(unit.phone_number || unit.email) && (
                                                <div>
                                                    {unit.phone_number && <div>📞 {unit.phone_number}</div>}
                                                    {unit.email && <div>✉️ {unit.email}</div>}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(unit.created_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Modifica su RECUP">
                                                <IconButton 
                                                    size="small" 
                                                    onClick={() => handleEditUnit(unit)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Righe per pagina:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                    }
                />
            </Paper>

            {/* Dialog Creazione Operation Unit */}
            <Dialog 
                open={showCreateDialog} 
                onClose={() => setShowCreateDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Crea Nuova Operation Unit su RECUP</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Codice Ministeriale *"
                                    value={createForm.ministerialCode}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, ministerialCode: e.target.value }))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Codice Regionale *"
                                    value={createForm.regionalCode}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, regionalCode: e.target.value }))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome Operation Unit *"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Tipo Identificatore Sede *"
                                    value={createForm.sedeContextIdentifierType}
                                    onChange={(e) => setCreateForm(prev => ({ 
                                        ...prev, 
                                        sedeContextIdentifierType: e.target.value as any
                                    }))}
                                    required
                                >
                                    <MenuItem value="CODICE_MINISTERIALE">Codice Ministeriale</MenuItem>
                                    <MenuItem value="CODICE_REGIONALE">Codice Regionale</MenuItem>
                                    <MenuItem value="ID_INTERNO">ID Interno</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Identificatore Sede *"
                                    value={createForm.sedeIdentifier}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, sedeIdentifier: e.target.value }))}
                                    required
                                    helperText="Codice della sede a cui appartiene l'operation unit"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Data Inizio *"
                                    value={createForm.startDate}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, startDate: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Data Fine"
                                    value={createForm.endDate || ''}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, endDate: e.target.value || undefined }))}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={createForm.disconnected}
                                            onChange={(e) => setCreateForm(prev => ({ ...prev, disconnected: e.target.checked }))}
                                        />
                                    }
                                    label="Disconnesso (disattivato)"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Header Promemoria"
                                    value={createForm.printHeader || ''}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, printHeader: e.target.value }))}
                                    multiline
                                    rows={3}
                                    helperText="Testo mostrato nell'header del promemoria appuntamento"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Footer Promemoria"
                                    value={createForm.printFooter || ''}
                                    onChange={(e) => setCreateForm(prev => ({ ...prev, printFooter: e.target.value }))}
                                    multiline
                                    rows={3}
                                    helperText="Testo mostrato nel footer del promemoria appuntamento"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCreateDialog(false)}>
                        Annulla
                    </Button>
                    <Button 
                        onClick={handleCreateUnit}
                        variant="contained"
                        disabled={formLoading || !createForm.ministerialCode || !createForm.name || !createForm.regionalCode || !createForm.sedeIdentifier}
                    >
                        {formLoading ? <CircularProgress size={20} /> : 'Crea su RECUP'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Modifica Operation Unit */}
            <Dialog 
                open={showEditDialog} 
                onClose={() => setShowEditDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Modifica Operation Unit su RECUP</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Stai modificando: <strong>{editingUnit?.name}</strong> (ID: {editingUnit?.id})
                        </Alert>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Codice Ministeriale"
                                    value={editForm.ministerialCode || ''}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, ministerialCode: e.target.value }))}
                                    helperText="Lascia vuoto se non vuoi modificarlo"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Codice Regionale"
                                    value={editForm.regionalCode || ''}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, regionalCode: e.target.value }))}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome Operation Unit"
                                    value={editForm.name || ''}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Data Inizio"
                                    value={editForm.startDate || ''}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, startDate: e.target.value }))}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Data Fine"
                                    value={editForm.endDate || ''}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, endDate: e.target.value || undefined }))}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editForm.disconnected || false}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, disconnected: e.target.checked }))}
                                        />
                                    }
                                    label="Disconnesso (disattivato)"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Header Promemoria"
                                    value={editForm.printHeader || ''}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, printHeader: e.target.value }))}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Footer Promemoria"
                                    value={editForm.printFooter || ''}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, printFooter: e.target.value }))}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowEditDialog(false)}>
                        Annulla
                    </Button>
                    <Button 
                        onClick={handleUpdateUnit}
                        variant="contained"
                        disabled={formLoading}
                        color="primary"
                    >
                        {formLoading ? <CircularProgress size={20} /> : 'Aggiorna su RECUP'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar per notifiche */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default OperationUnitsPage;